import { ModalBackdrop } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { ManualSupplierMergeDialog } from './ManualSupplierMergeDialog';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import '../styles/modules-supplier-consolidation.css';
import {
  supplierConsolidationApi,
  type SupplierConsolidationResult,
  type SupplierConsolidationSuggestionResult,
  type SupplierConsolidationSupplierResult,
} from './supplier-consolidation-api';

interface SupplierConsolidationPanelProps {
  readonly csrfToken: string;
  readonly onSupplierCountChange?: (count: number) => void;
}

export function SupplierConsolidationPanel({
  csrfToken,
  onSupplierCountChange,
}: SupplierConsolidationPanelProps): React.JSX.Element {
  const [result, setResult] = useState<SupplierConsolidationResult | null>(null);
  const [manualMergeOpen, setManualMergeOpen] = useState(false);
  const [reviewedSuggestion, setReviewedSuggestion] =
    useState<SupplierConsolidationSuggestionResult | null>(null);
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const [commonName, setCommonName] = useState('');
  const [deferredSuggestionIds, setDeferredSuggestionIds] = useState<readonly string[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void supplierConsolidationApi
      .read()
      .then((next) => {
        if (active) setResult(next);
      })
      .catch(() => {
        if (active) setError('La liste des fournisseurs ne peut pas être chargée.');
      });
    return () => {
      active = false;
    };
  }, []);

  const suppliersById = useMemo(
    () => new Map((result?.suppliers ?? []).map((supplier) => [supplier.id, supplier] as const)),
    [result],
  );
  const ungrouped = result?.suppliers.filter((supplier) => supplier.groupId === null) ?? [];

  useEffect(() => {
    if (result === null) return;
    const ungroupedCount = result.suppliers.filter((supplier) => supplier.groupId === null).length;
    onSupplierCountChange?.(result.groups.length + ungroupedCount);
  }, [onSupplierCountChange, result]);

  const actionableSuggestions =
    result?.suggestions.filter((suggestion) => !deferredSuggestionIds.includes(suggestion.id)) ??
    [];

  function review(suggestion: SupplierConsolidationSuggestionResult): void {
    setReviewedSuggestion(suggestion);
    setSelectedIds(suggestion.supplierReferenceIds);
    setCommonName(suggestion.proposedName);
    setError(null);
    setNotice(null);
  }

  function toggleSupplier(id: string): void {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((candidate) => candidate !== id) : [...current, id],
    );
  }

  async function consolidate(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (selectedIds.length < 2) {
      setError('Sélectionnez au moins deux fournisseurs à regrouper.');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await supplierConsolidationApi.consolidate(commonName, selectedIds, csrfToken);
      setResult(next);
      setReviewedSuggestion(null);
      setNotice('Le regroupement est enregistré et sera réutilisé lors des prochains imports.');
    } catch {
      setError('Ce regroupement ne peut pas être enregistré.');
    } finally {
      setBusy(false);
    }
  }

  function deferSuggestion(): void {
    if (reviewedSuggestion === null) return;
    setDeferredSuggestionIds((current) => [...current, reviewedSuggestion.id]);
    setReviewedSuggestion(null);
  }

  async function dismissSuggestion(): Promise<void> {
    if (reviewedSuggestion === null) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await supplierConsolidationApi.dismissSuggestion(
        reviewedSuggestion.supplierReferenceIds,
        csrfToken,
      );
      setResult(next);
      setReviewedSuggestion(null);
      setNotice('Ces fiches sont mémorisées comme fournisseurs différents.');
    } catch {
      setError('Cette décision ne peut pas être enregistrée.');
    } finally {
      setBusy(false);
    }
  }

  async function restoreSuggestion(exclusionId: string): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await supplierConsolidationApi.restoreSuggestion(exclusionId, csrfToken);
      setResult(next);
      setNotice('Le rapprochement pourra de nouveau être proposé.');
    } catch {
      setError('Cette exclusion ne peut pas être annulée.');
    } finally {
      setBusy(false);
    }
  }

  async function detach(
    groupId: string,
    supplier: SupplierConsolidationSupplierResult,
  ): Promise<void> {
    const confirmed = window.confirm(
      'Défusionner ' +
        supplier.displayName +
        ' ? Tosho mémorisera ce choix et ne le regroupera plus automatiquement.',
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await supplierConsolidationApi.detach(groupId, supplier.id, csrfToken);
      setResult(next);
      setNotice(
        supplier.displayName +
          ' est défusionné. Ses données Freebe restent intactes et son exclusion est mémorisée.',
      );
    } catch {
      setError('Ce fournisseur ne peut pas être défusionné.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="supplier-consolidation"
      aria-labelledby="supplier-consolidation-title"
      aria-busy={result === null && error === null}
    >
      <header className="supplier-consolidation__heading">
        <div>
          <p className="dashboard-panel__eyebrow">Qualité des données</p>
          <h2 id="supplier-consolidation-title">Consolidation des fournisseurs</h2>
          <p>
            Les fiches Freebe restent séparées. Tosho utilise un nom commun uniquement pour agréger
            les achats et les statistiques.
          </p>
        </div>
        <div className="supplier-consolidation__actions">
          <button
            type="button"
            disabled={busy || result === null}
            onClick={() => {
              setManualMergeOpen(true);
              setError(null);
              setNotice(null);
            }}
          >
            Fusionner des fournisseurs
          </button>
          <span className="supplier-consolidation__count">
            {result?.groups.length ?? 0} regroupement(s)
          </span>
        </div>
      </header>

      {error ? (
        <p className="supplier-consolidation__message tosho-feedback is-error" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="supplier-consolidation__message tosho-feedback is-success" role="status">
          {notice}
        </p>
      ) : null}

      {actionableSuggestions.length > 0 ? (
        <div className="supplier-consolidation__review">
          <div>
            <strong>{actionableSuggestions.length} rapprochement(s) à vérifier</strong>
            <span>
              Tosho a repéré des noms proches, mais attend ta confirmation avant de les regrouper.
            </span>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              const suggestion = actionableSuggestions[0];
              if (suggestion !== undefined) review(suggestion);
            }}
          >
            Examiner
          </button>
        </div>
      ) : null}

      <div className="supplier-consolidation__lists">
        <details>
          <summary>Regroupements actifs ({result?.groups.length ?? 0})</summary>
          <div className="supplier-consolidation__groups">
            {result?.groups.map((group) => (
              <article key={group.id} className="supplier-consolidation__group">
                <header>
                  <strong>{group.displayName}</strong>
                  <span>{group.members.length} fiche(s) Freebe</span>
                </header>
                <ul>
                  {group.members.map((supplier) => (
                    <li key={supplier.id}>
                      <span>
                        <strong>{supplier.displayName}</strong>
                        <small>
                          {supplier.membershipOrigin === 'AUTOMATIC_LEGAL_IDENTIFIER'
                            ? 'Regroupé automatiquement sur identifiant juridique'
                            : 'Regroupement validé manuellement'}
                        </small>
                      </span>
                      <button
                        type="button"
                        className="is-secondary"
                        disabled={busy}
                        onClick={() => void detach(group.id, supplier)}
                      >
                        Défusionner
                      </button>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
            {result !== null && result.groups.length === 0 ? (
              <p>Aucun regroupement n’est encore enregistré.</p>
            ) : null}
          </div>
        </details>

        <details>
          <summary>Rapprochements écartés ({result?.exclusions?.length ?? 0})</summary>
          <ul className="supplier-consolidation__ungrouped">
            {(result?.exclusions ?? []).map((exclusion) => (
              <li key={exclusion.id}>
                <strong>
                  {exclusion.leftSupplierName} ≠ {exclusion.rightSupplierName}
                </strong>
                <button
                  type="button"
                  className="is-secondary"
                  disabled={busy}
                  onClick={() => void restoreSuggestion(exclusion.id)}
                >
                  Réautoriser la suggestion
                </button>
              </li>
            ))}
          </ul>
        </details>

        <details>
          <summary>Fournisseurs non regroupés ({ungrouped.length})</summary>
          <ul className="supplier-consolidation__ungrouped">
            {ungrouped.map((supplier) => (
              <li key={supplier.id}>
                <strong>{supplier.displayName}</strong>
                {supplier.email ? <small>{supplier.email}</small> : null}
              </li>
            ))}
          </ul>
        </details>
      </div>

      {manualMergeOpen ? (
        <ManualSupplierMergeDialog
          csrfToken={csrfToken}
          onClose={() => setManualMergeOpen(false)}
          onConsolidated={(next) => {
            setResult(next);
            setManualMergeOpen(false);
            setNotice(
              'Le regroupement est enregistré et sera réutilisé lors des prochains imports.',
            );
          }}
        />
      ) : null}
      {reviewedSuggestion !== null ? (
        <ModalBackdrop
          className="supplier-consolidation__backdrop supplier-consolidation"
          aria-labelledby="supplier-consolidation-dialog-title"
          close={<ModalCloseButton disabled={busy} onClick={deferSuggestion} />}
        >
          <section className="tosho-modal-panel supplier-consolidation__dialog">
            <header className="tosho-modal-header">
              <p className="dashboard-panel__eyebrow">Validation nécessaire</p>
              <h3 id="supplier-consolidation-dialog-title">Regrouper ces fournisseurs ?</h3>
              <p>
                Coche les fiches qui représentent le même fournisseur, puis choisis leur nom commun.
              </p>
            </header>
            <form onSubmit={(event) => void consolidate(event)}>
              <fieldset>
                <legend>Fiches Freebe proposées</legend>
                {reviewedSuggestion.supplierReferenceIds.map((id) => {
                  const supplier = suppliersById.get(id);
                  if (supplier === undefined) return null;
                  return (
                    <label key={id}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(id)}
                        disabled={busy}
                        onChange={() => toggleSupplier(id)}
                      />
                      <span>
                        <strong>{supplier.displayName}</strong>
                        {supplier.email ? <small>{supplier.email}</small> : null}
                      </span>
                    </label>
                  );
                })}
              </fieldset>
              <label className="supplier-consolidation__common-name">
                <span>Nom de fournisseur commun</span>
                <input
                  required
                  maxLength={255}
                  value={commonName}
                  disabled={busy}
                  onChange={(event) => setCommonName(event.currentTarget.value)}
                />
              </label>
              <footer>
                <button
                  type="button"
                  className="is-secondary"
                  disabled={busy}
                  onClick={deferSuggestion}
                >
                  Plus tard
                </button>
                <button
                  type="button"
                  className="is-secondary"
                  disabled={busy}
                  onClick={() => void dismissSuggestion()}
                >
                  Fournisseurs différents
                </button>
                <button type="submit" disabled={busy || selectedIds.length < 2}>
                  {busy ? 'Enregistrement…' : 'Valider la fusion'}
                </button>
              </footer>
            </form>
          </section>
        </ModalBackdrop>
      ) : null}
    </section>
  );
}
